import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChatBot, Channel } from '../store/bots/botsApi';
import {
  useGetBotsQuery,
  useGetChannelsStatusQuery,
  useGetActiveByChannelQuery,
  useGetPricingPlansQuery,
  useGetUsageQuery,
  useRemoveBotMutation,
  useActivateForChannelMutation,
  useClearActiveForChannelMutation,
} from '../store/bots/botsApi';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faPlus,
  faRotateRight,
  faRobot,
  faSearch,
  faLock,
  faCheckCircle,
  faSlidersH,
  faEnvelope,
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
} from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { GhostSelect } from '../components/ui/GhostSelect';
import BtnTaratic from '../components/ui/BtnTaratic';
import { useDeleteConfirmation } from '../components/ui/DeleteConfirmationProvider';

const BotRow: React.FC<{
  bot: ChatBot;
  isActive?: boolean;
  onEdit: () => void;
  onDelete: () => Promise<void>;
}> = ({ bot, isActive, onEdit, onDelete }) => {
  const { t } = useTranslation();

  return (
    <div data-ui="bots-config-page-region-001"
      className={`p-4 rounded-xl flex flex-col gap-3 transition-all duration-300 shadow-inner-glow outline-none border-0 ${
        isActive
          ? 'bg-emerald-500/10 shadow-[inset_0_0_15px_rgba(16,185,129,0.25)]'
          : 'bg-white/5 shadow-[inset_0_0_12px_rgba(255,255,255,0.06)] hover:bg-white/[0.08] hover:shadow-[inset_0_0_16px_rgba(255,255,255,0.12)]'
      }`}
    >
      <div data-ui="bots-config-page-region-002" className="flex flex-wrap sm:flex-nowrap items-start sm:items-center justify-between gap-3 min-w-0">
        <div data-ui="bots-config-page-region-003" className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
          <div data-ui="bots-config-page-region-004" className="w-8 h-8 rounded-lg bg-pink-500/15 border border-pink-500/30 flex items-center justify-center shrink-0 shadow-[inset_0_0_8px_rgba(236,72,153,0.3)]">
            <FontAwesomeIcon icon={faRobot} className="text-pink-400 text-sm icon-inner-glow-white" />
          </div>
          <span className="text-base font-bold text-white tracking-wide truncate max-w-[180px] sm:max-w-[220px] md:max-w-xs">{bot.name}</span>
          <span className="text-xs px-2 py-0.5 rounded-full border border-white/15 bg-white/10 text-white/80 font-mono shrink-0">
            #{bot.id}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full border border-pink-500/30 bg-pink-500/10 text-pink-300 font-medium shadow-[inset_0_0_6px_rgba(236,72,153,0.2)] shrink-0">
            {bot.bot_type || 'general'}
          </span>
          {isActive && (
            <span className="text-xs px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/15 text-emerald-400 font-semibold flex items-center gap-1 shadow-[inset_0_0_6px_rgba(16,185,129,0.3)] shrink-0">
              <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />
              {t('botsConfigPage.bot.active', 'Bot Activo')}
            </span>
          )}
        </div>

        <div data-ui="bots-config-page-region-005" className="flex items-center gap-2 shrink-0 ml-auto sm:ml-0">
          <button
            className="btn-taratic btn-ghost-dark p-2 flex items-center justify-center rounded-full w-8 h-8 hover:bg-purple-500/20 hover:shadow-[inset_0_0_15px_rgba(168,85,247,0.5)] hover:border-purple-500/50 transition-all group"
            onClick={onEdit}
            title={t('botsConfigPage.buttons.edit', 'Editar')}
          >
            <FontAwesomeIcon icon={faEdit} className="text-white group-hover:text-purple-400 transition-colors" />
          </button>
          <button
            className="btn-taratic btn-ghost-dark p-2 flex items-center justify-center rounded-full w-8 h-8 hover:bg-red-500/20 hover:shadow-[inset_0_0_15px_rgba(239,68,68,0.5)] hover:border-red-500/50 transition-all group"
            onClick={onDelete}
            title={t('botsConfigPage.buttons.delete', 'Eliminar')}
          >
            <FontAwesomeIcon icon={faTrash} className="text-red-500 group-hover:text-red-400 transition-colors" />
          </button>
        </div>
      </div>

      <div data-ui="bots-config-page-region-006" className="bots-config-info text-xs text-white/70 flex flex-col gap-1.5 sm:pl-10 min-w-0">
        <p className="text-xs text-white/80 font-normal break-words">{bot.description || '—'}</p>
        <div data-ui="bots-config-page-region-007" className="bots-config-info flex flex-wrap items-center gap-x-4 gap-y-1 text-white/60">
          <span>
            <strong className="text-white/80">{t('botsConfigPage.bot.statusPrefix', 'Estado:')}</strong>{' '}
            {(bot as any).schedule_mode === 'disabled'
              ? t('botsConfigPage.bot.status.off', 'Desactivado')
              : (bot as any).schedule_mode === 'scheduled'
              ? t('botsConfigPage.bot.status.scheduled', 'Programado')
              : t('botsConfigPage.bot.status.always', 'Siempre activo')}
          </span>
          <span className="hidden sm:inline">•</span>
          <span>
            <strong className="text-white/80">{t('botsConfigPage.bot.channelsPrefix', 'Canales:')}</strong>{' '}
            {(bot.channels || []).length ? bot.channels?.join(', ') : t('botsConfigPage.bot.channelsAll', 'Todos los canales')}
          </span>
          <span className="hidden sm:inline">•</span>
          <span>
            <strong className="text-white/80">MCP (Herramientas):</strong>{' '}
            <span className={bot.enabled_mcps?.length ? 'text-emerald-400 font-bold' : 'text-white/40'}>
              {bot.enabled_mcps?.length
                ? bot.enabled_mcps.map((mcp) => t(`mcp.${mcp}`, { defaultValue: mcp })).join(', ')
                : 'Ninguna'}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

const BotsConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirmDelete = useDeleteConfirmation();

  const { data: bots = [], isFetching: botsLoading, refetch: refetchBots } = useGetBotsQuery();
  const { isFetching: plansLoading, refetch: refetchPlans } = useGetPricingPlansQuery();
  const { isFetching: channelsLoading, refetch: refetchChannels } = useGetChannelsStatusQuery();
  const { data: activeByChannel = { instagram: null, whatsapp: null, email: null }, isFetching: activeLoading, refetch: refetchActive } = useGetActiveByChannelQuery();
  const { data: usage = {}, isFetching: usageLoading, refetch: refetchUsage } = useGetUsageQuery();

  const [removeBotMutation] = useRemoveBotMutation();
  const [activateForChannel] = useActivateForChannelMutation();
  const [clearActiveForChannel] = useClearActiveForChannelMutation();

  const [isUpdatingChannel, setIsUpdatingChannel] = useState<Channel | null>(null);
  const [botSearchTerm, setBotSearchTerm] = useState<string>('');
  const [botSortMode, setBotSortMode] = useState<'latest' | 'name' | 'most_used'>('latest');

  // Estado de Paginación
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [inputPage, setInputPage] = useState<string>('1');

  useEffect(() => {
    setPage(1);
  }, [botSearchTerm, botSortMode]);

  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  const loading = botsLoading || plansLoading || channelsLoading || activeLoading || usageLoading;

  const refreshAll = useCallback(async () => {
    await Promise.all([refetchBots(), refetchPlans(), refetchChannels(), refetchActive(), refetchUsage()]);
  }, [refetchBots, refetchPlans, refetchChannels, refetchActive, refetchUsage]);

  const removeBotCtx = useCallback(async (id: number) => {
    return await removeBotMutation({ id }).unwrap();
  }, [removeBotMutation]);

  const loadAll = useCallback(async () => {
    await refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const deleteBot = async (id: number) => {
    if (await confirmDelete({
      title: t('botsConfigPage.buttons.delete', 'Eliminar bot'),
      message: t('botsConfigPage.confirmDelete') || '¿Estás seguro de eliminar este bot?',
    })) {
      try {
        await removeBotCtx(id);
        refreshAll();
      } catch (e: any) {
        alert(e?.message || t('botsConfigPage.errors.delete'));
      }
    }
  };

  const handleChannelBotChange = async (channel: Channel, selectedValue: string) => {
    setIsUpdatingChannel(channel);
    try {
      if (selectedValue === 'none') {
        await clearActiveForChannel({ channel }).unwrap();
      } else {
        await activateForChannel({ id: Number(selectedValue), channel }).unwrap();
      }
      await refreshAll();
    } catch (error: any) {
      alert(error?.data?.detail || error?.message || t('botsConfigPage.errors.update', 'Error al actualizar el bot activo del canal'));
    } finally {
      setIsUpdatingChannel(null);
    }
  };

  // Cálculo de bots filtrados, ordenados y paginados
  const filteredBots = bots.filter(b => (b.name || '').toLowerCase().includes(botSearchTerm.toLowerCase()));
  const sortedBots = filteredBots.slice().sort((a, b) => {
    if (botSortMode === 'name') {
      return (a.name || '').localeCompare(b.name || '');
    }
    if (botSortMode === 'most_used') {
      const ua = (usage as any)[a.id] || 0;
      const ub = (usage as any)[b.id] || 0;
      if (ub !== ua) return ub - ua;
      return String(b.created_at || '').localeCompare(String(a.created_at || ''));
    }
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });

  const totalBots = sortedBots.length;
  const totalPages = Math.ceil(totalBots / pageSize) || 1;
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startItem = totalBots === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endItem = Math.min(safePage * pageSize, totalBots);
  const paginatedBots = sortedBots.slice((safePage - 1) * pageSize, safePage * pageSize);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let start = Math.max(2, safePage - 1);
      let end = Math.min(totalPages - 1, safePage + 1);

      if (safePage <= 3) {
        end = 4;
      }
      if (safePage >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('...');
      }

      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div data-ui="bots-config-page" className="page-theme-surface page-theme-bots page-theme-blue-white p-4 md:p-6 lg:p-8 max-w-7xl mx-auto flex flex-col gap-6 animate-fade-in">
      <div data-ui="bots-config-page-region-008" className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div data-ui="bots-config-page-region-009">
          <h1 className="text-2xl md:text-3xl font-bold text-white">
            {t('botsConfigPage.title', 'Configuración de Agentes IA')}
          </h1>
          <p className="bots-config-info text-white/60 text-sm mt-1">{t('botsConfigPage.subtitle', 'Administra tus bots y asigna el agente por defecto para cada canal')}</p>
        </div>
        <BtnTaratic
          color="blue"
          size="sm"
          className="flex items-center gap-2"
          onClick={() => navigate('/bots-config/new')}
        >
          <FontAwesomeIcon icon={faPlus} />
          {t('botsConfigPage.create.buttons.create', 'Crear Nuevo Bot')}
        </BtnTaratic>
      </div>

      <div data-ui="bots-config-page-region-010" className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Lista de bots (Izquierda en Desktop, abajo en Mobile) */}
        {/* min-w-0 y overflow-x-hidden previenen que el grid item se desborde y se solape con Activación por canal */}
        <div data-ui="bots-config-page-region-011" className="lg:col-span-7 xl:col-span-7 min-w-0 order-2 lg:order-1 flex flex-col gap-4">
          <div data-ui="bots-config-page-region-012" className="bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md p-1 overflow-x-hidden">
            <div data-ui="bots-config-page-region-013" className="px-4 py-3 border-b border-white/10 font-medium flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-white">
              <span className="text-sm font-semibold flex items-center gap-2 shrink-0">
                <FontAwesomeIcon icon={faRobot} className="text-pink-400 text-base icon-inner-glow-white" />
                {t('botsConfigPage.list.title', 'Lista de Bots')} ({bots.length})
              </span>
              <div data-ui="bots-config-page-region-014" className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                <div data-ui="bots-config-page-region-015" className="relative flex-1 sm:flex-none sm:w-36 md:w-40 min-w-[120px]">
                  <input
                    type="text"
                    placeholder={t('botsConfigPage.list.searchPlaceholder', 'Buscar por nombre...')}
                    value={botSearchTerm}
                    onChange={(e) => setBotSearchTerm(e.target.value)}
                    className="w-full text-xs bg-black/20 border border-white/10 rounded-xl pl-8 pr-3 py-2 text-white placeholder-white/50 focus:bg-black/60 shadow-sm focus:outline-none focus:ring-1 focus:ring-pink-500/50 transition-all"
                  />
                  <FontAwesomeIcon icon={faSearch} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40 text-xs pointer-events-none" />
                </div>
                <div data-ui="bots-config-page-region-016" className="w-28 sm:w-32 shrink-0">
                  <GhostSelect
                    value={botSortMode}
                    onChange={(val: string) => setBotSortMode(val as any)}
                    options={[
                      { value: "latest", label: t('botsConfigPage.list.sort.latest') || "Más reciente" },
                      { value: "name", label: t('botsConfigPage.list.sort.name') || "Nombre" },
                      { value: "most_used", label: t('botsConfigPage.list.sort.most_used') || "Más usado" }
                    ]}
                  />
                </div>
                <BtnTaratic color="ghost-dark" size="xs" className="flex items-center gap-1.5 shrink-0" onClick={() => loadAll()}>
                  <FontAwesomeIcon icon={faRotateRight} />
                  {t('botsConfigPage.list.reload', 'Recargar')}
                </BtnTaratic>
              </div>
            </div>

            <div data-ui="bots-config-page-region-017" className="p-4 grid gap-3 min-h-[160px]">
              {loading ? (
                <div data-ui="bots-config-page-region-018" className="text-sm text-center py-8 text-white/50">{t('app.loading', 'Cargando...')}</div>
              ) : paginatedBots.length === 0 ? (
                <div data-ui="bots-config-page-region-019" data-tour="bots-config" className="text-sm text-center py-8 text-white/50">{t('botsConfigPage.list.empty', 'No se encontraron bots.')}</div>
              ) : (
                paginatedBots.map((b, index) => {
                  const isActive = Object.values(activeByChannel).some((activeBot: any) => activeBot?.id === b.id);
                  return (
                    <div data-ui="bots-config-page-region-020" key={b.id} id={`bot-row-${b.id}`} data-tour={index === 0 ? "bots-config" : undefined}>
                      <BotRow
                        bot={b}
                        isActive={isActive}
                        onEdit={() => navigate(`/bots-config/${b.id}`)}
                        onDelete={() => deleteBot(b.id)}
                      />
                      <div data-ui="bots-config-page-region-021" className="bots-config-info text-[11px] text-white/50 px-2 mt-1">{t('botsConfigPage.list.usageCount', { count: (usage as any)[b.id] || 0 })}</div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer de Paginación */}
            {totalBots > 0 && (
              <div data-ui="bots-config-page-region-022" className="px-4 py-3 border-t border-white/10 flex flex-col xl:flex-row items-center justify-between gap-3 text-xs text-white/70">
                <div data-ui="bots-config-page-region-023" className="flex items-center gap-3 justify-between sm:justify-start w-full xl:w-auto">
                  <span>
                    Mostrando <strong className="text-white font-medium">{startItem}</strong> a{' '}
                    <strong className="text-white font-medium">{endItem}</strong> de <strong className="text-white font-medium">{totalBots}</strong> bots
                  </span>
                  <div data-ui="bots-config-page-region-024" className="w-24 shrink-0">
                    <GhostSelect
                      value={String(pageSize)}
                      onChange={(val: string) => {
                        setPageSize(Number(val));
                        setPage(1);
                      }}
                      options={[
                        { value: '5', label: '5 / pág' },
                        { value: '10', label: '10 / pág' },
                        { value: '20', label: '20 / pág' },
                      ]}
                    />
                  </div>
                </div>

                <div data-ui="bots-config-page-region-025" className="flex items-center justify-between sm:justify-end gap-3 w-full xl:w-auto flex-wrap sm:flex-nowrap">
                  {/* Ir a pág */}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const p = parseInt(inputPage, 10);
                      if (!isNaN(p) && p >= 1 && p <= totalPages) {
                        setPage(p);
                      } else {
                        setInputPage(String(safePage));
                      }
                    }}
                    className="flex items-center gap-1.5 text-xs text-white/70"
                  >
                    <span className="whitespace-nowrap text-white/60">Ir a pág:</span>
                    <input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={inputPage}
                      onChange={(e) => setInputPage(e.target.value)}
                      onBlur={() => {
                        const p = parseInt(inputPage, 10);
                        if (isNaN(p) || p < 1 || p > totalPages) {
                          setInputPage(String(safePage));
                        }
                      }}
                      className="w-12 bg-black/20 border border-white/10 rounded-lg px-2 py-1 text-center text-white text-xs focus:outline-none focus:ring-1 focus:ring-pink-500/50"
                    />
                    <BtnTaratic type="submit" color="ghost-dark" size="xs" className="px-2 py-1">
                      Ir
                    </BtnTaratic>
                  </form>

                  {/* Botones de navegación con números de página idénticos a ClinicsPatientsPage */}
                  <div data-ui="bots-config-page-region-026" className="flex items-center gap-1">
                    <BtnTaratic
                      disabled={safePage === 1 || loading}
                      onClick={() => setPage(1)}
                      color="ghost-dark"
                      size="xs"
                      className="px-2 py-1 disabled:opacity-40"
                      title="Primera página"
                    >
                      <FontAwesomeIcon icon={faAnglesLeft} />
                    </BtnTaratic>
                    <BtnTaratic
                      disabled={safePage === 1 || loading}
                      onClick={() => setPage(safePage - 1)}
                      color="ghost-dark"
                      size="xs"
                      className="px-2 py-1 disabled:opacity-40"
                      title="Página anterior"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </BtnTaratic>

                    <div data-ui="bots-config-page-region-027" className="hidden sm:flex items-center gap-1">
                      {getPageNumbers().map((pNum, idx) =>
                        typeof pNum === 'number' ? (
                          <BtnTaratic
                            key={idx}
                            onClick={() => setPage(pNum)}
                            color={safePage === pNum ? 'pink' : 'ghost-dark'}
                            size="xs"
                            className={`min-w-[28px] px-2 py-1 font-medium ${safePage === pNum ? 'shadow-sm' : ''}`}
                          >
                            {pNum}
                          </BtnTaratic>
                        ) : (
                          <span key={idx} className="px-1 text-white/40 text-xs select-none">
                            {pNum}
                          </span>
                        )
                      )}
                    </div>

                    <span className="sm:hidden px-2 text-white/80 font-medium select-none">
                      {safePage} / {totalPages}
                    </span>

                    <BtnTaratic
                      disabled={safePage >= totalPages || loading}
                      onClick={() => setPage(safePage + 1)}
                      color="ghost-dark"
                      size="xs"
                      className="px-2 py-1 disabled:opacity-40"
                      title="Página siguiente"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </BtnTaratic>
                    <BtnTaratic
                      disabled={safePage >= totalPages || loading}
                      onClick={() => setPage(totalPages)}
                      color="ghost-dark"
                      size="xs"
                      className="px-2 py-1 disabled:opacity-40"
                      title="Última página"
                    >
                      <FontAwesomeIcon icon={faAnglesRight} />
                    </BtnTaratic>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Activación por canal (Derecha en Desktop, arriba en Mobile) */}
        <div data-ui="bots-config-page-region-028" className="lg:col-span-5 xl:col-span-5 min-w-0 order-1 lg:order-2 flex flex-col gap-4">
          <div data-ui="bots-config-page-region-029" className="flex flex-col gap-1">
            <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
              <div data-ui="bots-config-page-region-030" className="w-8 h-8 rounded-xl icon-box-glow-pink flex items-center justify-center shrink-0">
                <FontAwesomeIcon icon={faSlidersH} className="text-pink-400 text-sm icon-inner-glow-white" />
              </div>
              {t('botsConfigPage.channels.title', 'Activación por canal')}
            </h2>
            <p className="bots-config-info text-xs text-white/60">
              {t('botsConfigPage.channels.subtitle', 'Selecciona qué Agente IA atiende las conversaciones en cada canal de comunicación')}
            </p>
          </div>

          <div data-ui="bots-config-page-region-031" className="flex flex-col gap-4">
            {(['instagram', 'whatsapp', 'email'] as const).map((ch) => {
              const isBlocked = ch === 'whatsapp' || ch === 'email';
              const active = activeByChannel[ch];

              const channelIcon = ch === 'instagram' ? faInstagram : ch === 'whatsapp' ? faWhatsapp : faEnvelope;
              const channelColor = ch === 'instagram' ? 'text-pink-400' : ch === 'whatsapp' ? 'text-emerald-400' : 'text-sky-400';
              const glowClass = ch === 'instagram' ? 'icon-box-glow-pink' : ch === 'whatsapp' ? 'icon-box-glow-emerald' : 'icon-box-glow-sky';

              return (
                <div data-ui="bots-config-page-region-032"
                  key={ch}
                  className={`p-5 rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md flex flex-col justify-between gap-4 relative overflow-hidden transition-all duration-300 ${
                    isBlocked ? 'opacity-65 cursor-not-allowed' : 'hover:shadow-[0_0_25px_rgba(236,72,153,0.08)]'
                  }`}
                >
                  {/* Header del canal */}
                  <div data-ui="bots-config-page-region-033" className="flex items-center justify-between gap-3 border-b border-white/10 pb-3">
                    <div data-ui="bots-config-page-region-034" className="flex items-center gap-3">
                      <div data-ui="bots-config-page-region-035" className={`w-9 h-9 rounded-xl flex items-center justify-center ${glowClass}`}>
                        <FontAwesomeIcon icon={channelIcon} className={`text-base ${channelColor} icon-inner-glow-white`} />
                      </div>
                      <div data-ui="bots-config-page-region-036" className="flex flex-col">
                        <span className="text-sm font-bold text-white capitalize tracking-wide">
                          {ch}
                        </span>
                        {!isBlocked && (
                          <span className="bots-config-info text-[11px] text-white/50">
                            {active ? 'Con Bot Activo' : 'Sin Bot Asignado'}
                          </span>
                        )}
                      </div>
                    </div>

                    {isBlocked ? (
                      <span className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                        <FontAwesomeIcon icon={faLock} className="text-[10px]" />
                        (Bloqueado)
                      </span>
                    ) : (
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${
                        active ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' : 'text-white/40 bg-white/5 border border-white/10'
                      }`}>
                        <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />
                        {active ? 'Activo' : 'Inactivo'}
                      </span>
                    )}
                  </div>

                  {/* Contenido del canal */}
                  {isBlocked ? (
                    <div data-ui="bots-config-page-region-037" className="bots-config-info bots-config-info-panel py-6 rounded-xl bg-white/5 shadow-inner-glow shadow-inner-glow-black-md flex flex-col items-center justify-center gap-2 text-center text-white/40 border-0">
                      <div data-ui="bots-config-page-region-038" className="w-10 h-10 rounded-xl icon-box-glow-gray flex items-center justify-center mb-1">
                        <FontAwesomeIcon icon={faLock} className="text-base text-white/60 icon-inner-glow-white" />
                      </div>
                      <span className="text-xs font-medium italic">Canal no disponible actualmente</span>
                    </div>
                  ) : (
                    <div data-ui="bots-config-page-region-039" className="flex flex-col gap-4">
                      {/* Selector de bot para el canal */}
                      <div data-ui="bots-config-page-region-040" className="flex flex-col gap-1.5">
                        <label className="bots-config-info text-xs font-medium text-white/70">
                          {t('botsConfigPage.channels.selectLabel', 'Bot Por Defecto:')}
                        </label>
                        <GhostSelect
                          disabled={isUpdatingChannel === ch}
                          value={active ? String(active.id) : 'none'}
                          onChange={(val: string) => handleChannelBotChange(ch, val)}
                          options={[
                            { value: 'none', label: 'Sin bot activo' },
                            ...bots.map((b) => ({
                              value: String(b.id),
                              label: b.name,
                              subtitle: `${b.description || 'Sin descripción'} • rol: ${b.bot_type || 'general'}`,
                            })),
                          ]}
                        />
                      </div>

                      {/* Previsualización del Bot Seleccionado */}
                      {active ? (
                        <div data-ui="bots-config-page-region-041" className="bots-config-info-panel p-3.5 rounded-xl bg-white/5 shadow-inner-glow shadow-inner-glow-black-md flex flex-col gap-2 relative overflow-hidden border-0">
                          <div data-ui="bots-config-page-region-042" className="flex items-center justify-between gap-2">
                            <span className="text-sm font-bold text-white tracking-wide">
                              {active.name}
                            </span>
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-pink-500/15 border border-pink-500/30 text-pink-300 font-medium">
                              rol: {active.bot_type || 'general'}
                            </span>
                          </div>
                          <p className="bots-config-info text-xs text-white/70 leading-relaxed">
                            {active.description || 'Bot para agendar citas y de primer contacto'}
                          </p>
                          <div data-ui="bots-config-page-region-043" className="bots-config-info flex items-center gap-3 text-[11px] text-white/50 pt-1 border-t border-white/5 mt-1">
                            <span>ID: #{active.id}</span>
                            <span>•</span>
                            <span>Canales: {active.channels?.length ? active.channels.join(', ') : 'Todos'}</span>
                          </div>
                        </div>
                      ) : (
                        <div data-ui="bots-config-page-region-044" className="bots-config-info bots-config-info-panel p-3.5 rounded-xl bg-white/5 shadow-inner-glow shadow-inner-glow-black-md border-2 border-dashed border-white/10 text-center text-xs text-white/50 italic py-5">
                          Selecciona un bot arriba para asignarlo a {ch}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotsConfigPage;
